import { normalizeToTimestamp } from 'src/utils/dateUtils';

/**
 * Formats a date/timestamp for display with a time component,
 * e.g. `"Jun 15, 2024, 12:00 PM"`. Returns `'-'` for null /
 * undefined / invalid input.
 *
 * Use this for surfaces that need to communicate WHEN something
 * happened at wall-clock precision — the last-updated badge on
 * the perform-audit page, the version-conflict modal, activity
 * timelines. For date-only entity fields (audit due date, task
 * completion date, etc.) keep using {@link formatDisplayDate};
 * that formatter's single-date-per-row spacing budget doesn't
 * accommodate a time suffix.
 *
 * Renders in the viewer's locale + local timezone via
 * `toLocaleString`. Composed as two `Intl.DateTimeFormat` parts
 * (date + time) joined by a comma so the output stays consistent
 * with {@link formatDisplayDate}'s short-month/short-year shape
 * rather than picking up `toLocaleString`'s default "at" glue,
 * which reads awkwardly next to the phonetool alias link.
 */
export function formatDisplayDateTime(
  ts: Date | string | number | null | undefined,
): string {
  const ms = normalizeToTimestamp(ts);
  if (!ms) return '-';
  const date = new Date(ms);
  const datePart = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
  const timePart = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
  return `${datePart}, ${timePart}`;
}
